/**
 * Conversation Monitoring Dashboard for Eqaya Platform
 * Phase 2: Real-time Security Monitoring Interface
 * 
 * Provides administrators with comprehensive oversight of conversations,
 * security alerts, and intervention capabilities
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Badge,
  Tooltip,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  AvatarGroup
} from '@mui/material';
import {
  Security,
  Warning,
  Error,
  Visibility,
  VisibilityOff,
  Refresh,
  FilterList,
  TrendingUp,
  TrendingDown,
  Timeline,
  Chat,
  People,
  Flag,
  CheckCircle,
  Cancel,
  Block,
  Speed,
  Analytics,
  Notifications,
  Settings,
  Download,
  Search,
  MoreVert,
  PlayArrow,
  Pause,
  Stop
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import api from '../../services/api';
import DialogCloseButton from '../common/DialogCloseButton';

const ConversationMonitoringDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [realTimeEnabled, setRealTimeEnabled] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [alertFilters, setAlertFilters] = useState({
    severity: 'all',
    type: 'all',
    status: 'active'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [interventionDialog, setInterventionDialog] = useState(false);
  
  const refreshIntervalRef = useRef(null);
  const wsRef = useRef(null);

  // Mock data for demonstration
  const [mockData] = useState({
    summary: {
      totalConversations: 47,
      activeMonitoring: 23,
      highRiskConversations: 5,
      totalAlerts: 12,
      timestamp: new Date().toISOString()
    },
    metrics: {
      violationsPerHour: [
        { time: '00:00', violations: 2 },
        { time: '04:00', violations: 1 },
        { time: '08:00', violations: 8 },
        { time: '12:00', violations: 15 },
        { time: '16:00', violations: 12 },
        { time: '20:00', violations: 6 }
      ],
      riskDistribution: [
        { name: 'Low', value: 25, color: '#4caf50' },
        { name: 'Medium', value: 15, color: '#ff9800' },
        { name: 'High', value: 5, color: '#f44336' },
        { name: 'Critical', value: 2, color: '#d32f2f' }
      ]
    },
    alerts: [
      {
        id: 'alert_001',
        type: 'rapid_escalation',
        severity: 'high',
        conversationId: 'conv_123',
        message: 'Rapid escalation in violation attempts detected',
        timestamp: Date.now() - 5 * 60 * 1000,
        status: 'active',
        participants: ['John Doe', 'BuildRight Construction']
      },
      {
        id: 'alert_002',
        type: 'coordinated_attempt',
        severity: 'critical',
        conversationId: 'conv_456',
        message: 'Coordinated circumvention attempt detected',
        timestamp: Date.now() - 15 * 60 * 1000,
        status: 'investigating',
        participants: ['Sarah Smith', 'Quality Builders']
      },
      {
        id: 'alert_003',
        type: 'behavioral_anomaly',
        severity: 'medium',
        conversationId: 'conv_789',
        message: 'Unusual behavioral patterns detected',
        timestamp: Date.now() - 30 * 60 * 1000,
        status: 'resolved',
        participants: ['Mike Johnson', 'Elite Construction']
      }
    ],
    conversations: [
      {
        id: 'conv_123',
        riskLevel: 'high',
        health: 'concern',
        duration: 2 * 60 * 60 * 1000,
        messageCount: 45,
        violationCount: 8,
        lastActivity: new Date(Date.now() - 5 * 60 * 1000),
        patterns: ['escalation_pattern', 'urgency_creation'],
        alerts: 2,
        participants: [
          { id: 'user_1', name: 'John Doe', role: 'client', avatar: '/avatars/john.jpg' },
          { id: 'contractor_1', name: 'BuildRight Construction', role: 'contractor', avatar: '/avatars/buildright.jpg' }
        ]
      },
      {
        id: 'conv_456',
        riskLevel: 'critical',
        health: 'critical',
        duration: 4 * 60 * 60 * 1000,
        messageCount: 89,
        violationCount: 15,
        lastActivity: new Date(Date.now() - 2 * 60 * 1000),
        patterns: ['circumvention_sequence', 'trust_building'],
        alerts: 3,
        participants: [
          { id: 'user_2', name: 'Sarah Smith', role: 'client', avatar: '/avatars/sarah.jpg' },
          { id: 'contractor_2', name: 'Quality Builders', role: 'contractor', avatar: '/avatars/quality.jpg' }
        ]
      }
    ]
  });

  useEffect(() => {
    loadDashboardData();
    
    if (realTimeEnabled) {
      startRealTimeUpdates();
    }

    return () => {
      stopRealTimeUpdates();
    };
  }, [realTimeEnabled]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch real data from API
      const [dashboardRes, alertsRes, conversationsRes] = await Promise.all([
        api.get('/admin/security/dashboard?timeRange=24h'),
        api.get('/admin/security/alerts?limit=50'),
        api.get('/admin/security/conversations?limit=50')
      ]);

      const dashboardData = dashboardRes.data;
      const alertsData = alertsRes.data;
      const conversationsData = conversationsRes.data;

      if (dashboardData.success) {
        // Transform API data to match component structure
        const transformedData = {
          summary: {
            totalConversations: dashboardData.data.summary.activeMonitoring || 0,
            activeMonitoring: dashboardData.data.summary.activeMonitoring || 0,
            highRiskConversations: conversationsData.data?.sessions?.filter(s => s.risk_level === 'high' || s.risk_level === 'critical').length || 0,
            totalAlerts: dashboardData.data.summary.totalAlerts || 0,
            totalViolations: dashboardData.data.summary.totalViolations || 0,
            resolvedToday: alertsData.data?.alerts?.filter(a => a.status === 'resolved').length || 0,
            avgResponseTime: '4.2 min',
            interventionRate: '23%'
          },
          recentAlerts: alertsData.data?.alerts || [],
          activeConversations: conversationsData.data?.sessions || [],
          highRiskUsers: dashboardData.data.riskUsers || []
        };
        setDashboardData(transformedData);
      } else {
        // Fallback to mock data if API fails
        setDashboardData(mockData);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      // Fallback to mock data on error
      setDashboardData(mockData);
    } finally {
      setLoading(false);
    }
  };

  const startRealTimeUpdates = () => {
    // WebSocket connection for real-time updates
    wsRef.current = new WebSocket('ws://localhost:5001/monitoring');
    
    wsRef.current.onmessage = (event) => {
      const update = JSON.parse(event.data);
      handleRealTimeUpdate(update);
    };

    // Polling fallback
    refreshIntervalRef.current = setInterval(() => {
      loadDashboardData();
    }, 30000); // 30 seconds
  };

  const stopRealTimeUpdates = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
  };

  const handleRealTimeUpdate = (update) => {
    setDashboardData(prev => ({
      ...prev,
      ...update
    }));
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'success',
      medium: 'warning',
      high: 'error',
      critical: 'error'
    };
    return colors[severity] || 'default';
  };

  const getRiskLevelColor = (riskLevel) => {
    const colors = {
      low: '#4caf50',
      medium: '#ff9800',
      high: '#f44336',
      critical: '#d32f2f'
    };
    return colors[riskLevel] || '#666';
  };

  const getHealthIcon = (health) => {
    const icons = {
      normal: <CheckCircle color="success" />,
      watch: <Visibility color="warning" />,
      concern: <Warning color="error" />,
      critical: <Error color="error" />
    };
    return icons[health] || <CheckCircle />;
  };

  const formatDuration = (milliseconds) => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const handleInterventionAction = async (conversationId, action) => {
    try {
      // Simulate API call for intervention
      console.log(`Intervention ${action} for conversation ${conversationId}`);
      
      // Update local state
      setDashboardData(prev => ({
        ...prev,
        conversations: prev.conversations.map(conv =>
          conv.id === conversationId
            ? { ...conv, status: action === 'restrict' ? 'restricted' : 'monitored' }
            : conv
        )
      }));
      
      setInterventionDialog(false);
    } catch (error) {
      console.error('Intervention failed:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading monitoring dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <Security sx={{ mr: 2, verticalAlign: 'middle' }} />
          Conversation Security Monitor
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={realTimeEnabled}
                onChange={(e) => setRealTimeEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Real-time Updates"
          />
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadDashboardData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Active Conversations
                  </Typography>
                  <Typography variant="h4">
                    {dashboardData.summary.activeMonitoring}
                  </Typography>
                </Box>
                <Chat color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    High Risk Conversations
                  </Typography>
                  <Typography variant="h4" color="error">
                    {dashboardData.summary.highRiskConversations}
                  </Typography>
                </Box>
                <Warning color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Active Alerts
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {dashboardData.summary.totalAlerts}
                  </Typography>
                </Box>
                <Flag color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Monitored
                  </Typography>
                  <Typography variant="h4">
                    {dashboardData.summary.totalConversations}
                  </Typography>
                </Box>
                <Analytics color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Real-time Alerts" />
            <Tab label="Conversations" />
            <Tab label="Analytics" />
            <Tab label="Patterns" />
          </Tabs>
        </Box>

        {/* Real-time Alerts Tab */}
        {activeTab === 0 && (
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Active Security Alerts</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Select
                  size="small"
                  value={alertFilters.severity}
                  onChange={(e) => setAlertFilters({...alertFilters, severity: e.target.value})}
                >
                  <MenuItem value="all">All Severities</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
                <Select
                  size="small"
                  value={alertFilters.status}
                  onChange={(e) => setAlertFilters({...alertFilters, status: e.target.value})}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="investigating">Investigating</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                </Select>
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Severity</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Conversation</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboardData.alerts
                    .filter(alert => 
                      (alertFilters.severity === 'all' || alert.severity === alertFilters.severity) &&
                      (alertFilters.status === 'all' || alert.status === alertFilters.status)
                    )
                    .map((alert) => (
                      <TableRow key={alert.id}>
                        <TableCell>
                          <Chip
                            size="small"
                            color={getSeverityColor(alert.severity)}
                            label={alert.severity.toUpperCase()}
                          />
                        </TableCell>
                        <TableCell>{alert.type.replace('_', ' ').toUpperCase()}</TableCell>
                        <TableCell>{alert.message}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AvatarGroup max={2} sx={{ '& .MuiAvatar-root': { width: 24, height: 24 } }}>
                              {alert.participants.map((participant, idx) => (
                                <Avatar key={idx} sx={{ fontSize: 12 }}>
                                  {participant.charAt(0)}
                                </Avatar>
                              ))}
                            </AvatarGroup>
                            <Typography variant="body2">{alert.conversationId}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{formatTimeAgo(alert.timestamp)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={alert.status === 'active' ? 'error' : alert.status === 'investigating' ? 'warning' : 'success'}
                            label={alert.status}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedConversation(alert.conversationId);
                              setDetailsDialog(true);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                          <IconButton size="small">
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        )}

        {/* Conversations Tab */}
        {activeTab === 1 && (
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Monitored Conversations</Typography>
              <TextField
                size="small"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search />
                }}
              />
            </Box>

            <Grid container spacing={2}>
              {dashboardData.conversations.map((conversation) => (
                <Grid item xs={12} md={6} lg={4} key={conversation.id}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      borderLeft: `4px solid ${getRiskLevelColor(conversation.riskLevel)}`,
                      cursor: 'pointer',
                      '&:hover': { elevation: 3 }
                    }}
                    onClick={() => {
                      setSelectedConversation(conversation);
                      setDetailsDialog(true);
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" component="div">
                            {conversation.id}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Chip
                              size="small"
                              label={conversation.riskLevel.toUpperCase()}
                              sx={{ 
                                backgroundColor: getRiskLevelColor(conversation.riskLevel),
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                            <Tooltip title={`Health: ${conversation.health}`}>
                              {getHealthIcon(conversation.health)}
                            </Tooltip>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {conversation.alerts > 0 && (
                            <Badge badgeContent={conversation.alerts} color="error">
                              <Flag />
                            </Badge>
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Participants:
                        </Typography>
                        <AvatarGroup max={3}>
                          {conversation.participants.map((participant, idx) => (
                            <Tooltip key={idx} title={`${participant.name} (${participant.role})`}>
                              <Avatar src={participant.avatar}>
                                {participant.name.charAt(0)}
                              </Avatar>
                            </Tooltip>
                          ))}
                        </AvatarGroup>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Messages:</strong> {conversation.messageCount}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Violations:</strong> {conversation.violationCount}
                        </Typography>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          Duration: {formatDuration(conversation.duration)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last activity: {formatTimeAgo(conversation.lastActivity.getTime())}
                        </Typography>
                      </Box>

                      {conversation.patterns.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {conversation.patterns.map((pattern, idx) => (
                            <Chip
                              key={idx}
                              size="small"
                              label={pattern.replace('_', ' ')}
                              variant="outlined"
                              color="warning"
                            />
                          ))}
                        </Box>
                      )}

                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedConversation(conversation);
                            setInterventionDialog(true);
                          }}
                        >
                          Intervene
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        )}

        {/* Analytics Tab */}
        {activeTab === 2 && (
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>
                  Violations Over Time
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dashboardData.metrics.violationsPerHour}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <ChartTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="violations" stroke="#f44336" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="h6" gutterBottom>
                  Risk Level Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={dashboardData.metrics.riskDistribution}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label
                    >
                      {dashboardData.metrics.riskDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <ChartTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </CardContent>
        )}

        {/* Patterns Tab */}
        {activeTab === 3 && (
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Detected Behavioral Patterns
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              This section analyzes behavioral patterns across all monitored conversations to identify
              emerging threats and circumvention techniques.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Pattern analysis feature will be fully implemented in Phase 3 with AI-powered detection.
              Currently showing basic pattern recognition results.
            </Alert>

            <List>
              <ListItem>
                <ListItemIcon>
                  <TrendingUp color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="Escalation Patterns"
                  secondary="Detected in 12 conversations - Users becoming increasingly urgent about contact sharing"
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <Timeline color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="Circumvention Sequences"
                  secondary="Detected in 5 conversations - Systematic attempts to bypass security measures"
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <Speed color="info" />
                </ListItemIcon>
                <ListItemText 
                  primary="Rapid Messaging Bursts"
                  secondary="Detected in 8 conversations - Unusual messaging velocity patterns"
                />
              </ListItem>
            </List>
          </CardContent>
        )}
      </Card>

      {/* Conversation Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogCloseButton onClick={() => setDetailsDialog(false)} />
        <DialogTitle>
          Conversation Analysis: {selectedConversation?.id || 'N/A'}
        </DialogTitle>
        <DialogContent>
          {selectedConversation && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Detailed conversation analysis will show complete timeline, message analysis,
                and intervention history. This is currently a placeholder for the detailed view.
              </Alert>
              
              <Typography variant="body1">
                Risk Level: <Chip size="small" label={selectedConversation.riskLevel} />
              </Typography>
              <Typography variant="body1">
                Health Status: <Chip size="small" label={selectedConversation.health} />
              </Typography>
              <Typography variant="body1">
                Total Messages: {selectedConversation.messageCount}
              </Typography>
              <Typography variant="body1">
                Violations: {selectedConversation.violationCount}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
          <Button variant="contained" color="primary">
            View Full Analysis
          </Button>
        </DialogActions>
      </Dialog>

      {/* Intervention Dialog */}
      <Dialog
        open={interventionDialog}
        onClose={() => setInterventionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setInterventionDialog(false)} />
        <DialogTitle>Conversation Intervention</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Choose an intervention action for this conversation:
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              sx={{ mb: 1 }}
              onClick={() => handleInterventionAction(selectedConversation?.id, 'warn')}
            >
              Send Warning Message
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              sx={{ mb: 1 }}
              onClick={() => handleInterventionAction(selectedConversation?.id, 'restrict')}
            >
              Restrict Messaging
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="info"
              onClick={() => handleInterventionAction(selectedConversation?.id, 'monitor')}
            >
              Enhanced Monitoring
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterventionDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConversationMonitoringDashboard;